<script setup lang="ts">
import { useVModel } from '@vueuse/core'

import RewardInfosBlock from '@/components/rewards/RewardInfosBlock.vue'
import RewardTypeBlock from '@/components/rewards/RewardTypeBlock.vue'

import type { PartnerWithAccounts } from '@/types/offer'
import type { LoyaltyReward, RewardUsage } from '@/types/types'

import RewardAccessConditionsBlock from './RewardAccessConditionsBlock.vue'
import RewardSummary from './RewardSummary.vue'
import { useRewardForm } from './compmosable/useRewardForm'

const props = withDefaults(
  defineProps<{
    editingReward: LoyaltyReward
    usage?: RewardUsage
    sideitemTopOffset?: string
    conditionsTouched?: boolean
    marketplacePartners?: PartnerWithAccounts[]
    isLoadingMarketplacePartners?: boolean
    hideClaimLimitBlock?: boolean
  }>(),
  {
    sideitemTopOffset: 'top-6',
    marketplacePartners: () => [],
    isLoadingMarketplacePartners: false,
    hideClaimLimitBlock: false
  }
)

const emit = defineEmits<{
  (e: 'update:conditionsTouched', value: boolean): void
  (e: 'update:nameChanged', name: string): void
}>()

const editingReward = useVModel(props, 'editingReward')
const conditionsTouched = useVModel(props, 'conditionsTouched', emit)

const { firstRewardFormError } = useRewardForm()

const rewardInfosBlockRef = ref<InstanceType<typeof RewardInfosBlock> | null>(null)
const rewardTypeBlockRef = ref<InstanceType<typeof RewardTypeBlock> | null>(null)
const accessConditionsBlockRef = ref<InstanceType<typeof RewardAccessConditionsBlock> | null>(null)

const hasAccessErrors = computed(() => accessConditionsBlockRef.value?.hasAccessErrors ?? false)

const focusFirstError = (): boolean => {
  if (!editingReward.value?.name?.trim()) {
    rewardInfosBlockRef.value?.setNameError()
    return true
  }

  const field = firstRewardFormError.value
  if (!field) return false
  const hasError = rewardTypeBlockRef.value?.focusError(field)
  if (hasError) return true
  if (accessConditionsBlockRef.value?.focusError(field)) return true
  return false
}

defineExpose({
  hasAccessErrors,
  accessConditionsBlockRef,
  focusFirstError
})
</script>

<template>
  <div class="flex-1 lg:max-w-4xl">
    <div class="w-full flex flex-col gap-6">
      <RewardInfosBlock ref="rewardInfosBlockRef" v-model:editing-reward="editingReward" />

      <RewardTypeBlock
        ref="rewardTypeBlockRef"
        v-model:editing-reward="editingReward"
        :marketplace-partners="marketplacePartners"
        :is-loading-marketplace-partners="isLoadingMarketplacePartners"
      />

      <RewardAccessConditionsBlock
        ref="accessConditionsBlockRef"
        v-model:editing-reward="editingReward"
        v-model:form-touched="conditionsTouched"
        :marketplace-partners="marketplacePartners"
        :is-loading-marketplace-partners="isLoadingMarketplacePartners"
      />

      <slot name="additional-form-blocks" />
    </div>
  </div>
  <div class="flex flex-col lg:w-sm w-full">
    <RewardSummary :reward="editingReward" :sideitem-top-offset="sideitemTopOffset">
      <template #additional-summary>
        <slot name="additional-summary" />
      </template>
    </RewardSummary>
  </div>
</template>

<style scoped>
.partner-list-enter-active,
.partner-list-leave-active {
  transition: all 0.3s ease;
}

.partner-list-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.partner-list-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.partner-list-move {
  transition: transform 0.3s ease;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
.fade-slide-enter-to,
.fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
